/**
 * File: useProjectState.ts
 * Description: React hook for managing project state across all pages
 * Author: System
 * Created: 2025-08-11
 * Last Modified: 2025-08-11
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { 
  projectStateService, 
  ProjectState, 
  ProjectSetupState,
  ContextManagementState,
  CRAState,
  ADRState,
  GenerationState,
  ReviewState,
} from '../services/projectStateService';
import { ProjectStatus } from '../types/project';
import { logger } from '../utils/logger';

// ======================
// Hook Types
// ======================

export interface UseProjectStateReturn<T> {
  state: T | null;
  isLoading: boolean;
  error: string | null;
  updateState: (newState: Partial<T>) => Promise<void>;
  saveState: () => Promise<void>;
  resetState: () => void;
  lastSaved: string | null;
}

export interface UseProjectStateOptions {
  autoSave?: boolean;
  autoSaveDelay?: number;
  enableErrorRetry?: boolean;
  maxRetries?: number;
}

// ======================
// Main Hook
// ======================

/**
 * Main hook for managing project state
 */
export function useProjectState(): {
  projectState: ProjectState | null;
  isLoading: boolean;
  error: string | null;
  loadProject: (projectId: number) => Promise<void>;
  updateProjectStatus: (status: ProjectStatus) => Promise<void>;
  getLastPageRoute: () => Promise<string>;
  } {
  const [projectState, setProjectState] = useState<ProjectState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProject = useCallback(async (projectId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const state = await projectStateService.getProjectState(projectId);
      setProjectState(state);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProjectStatus = useCallback(async (status: ProjectStatus) => {
    if (!projectState) {return;}
    
    try {
      await projectStateService.updateProjectStatus(projectState.project.id, status);
      setProjectState(prev => prev ? {
        ...prev,
        project: { ...prev.project, status },
      } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project status');
    }
  }, [projectState]);

  const getLastPageRoute = useCallback(async () => {
    if (!projectState) {return '/';}
    
    // Convert string status to enum
    const statusMap: Record<string, ProjectStatus> = {
      'context_upload': ProjectStatus.CONTEXT_UPLOAD,
      'context_review': ProjectStatus.CONTEXT_REVIEW,
      'adr': ProjectStatus.ADR,
      'generation': ProjectStatus.GENERATION,
      'bizzdesign_integration': ProjectStatus.BIZZDESIGN_INTEGRATION,
      'completed': ProjectStatus.COMPLETED,
    };
    
    const statusEnum = statusMap[projectState.project.status] ?? ProjectStatus.CONTEXT_UPLOAD;
    
    return projectStateService.getLastPageRoute(
      projectState.project.id, 
      statusEnum,
    );
  }, [projectState]);

  return {
    projectState,
    isLoading,
    error,
    loadProject,
    updateProjectStatus,
    getLastPageRoute,
  };
}

// ======================
// Page-Specific Hooks
// ======================

/**
 * Hook for Project Setup page state
 */
export function useProjectSetupState(
  projectId: number,
  options: UseProjectStateOptions = {},
): UseProjectStateReturn<ProjectSetupState> {
  return usePageState<ProjectSetupState>(projectId, 'setup', options);
}

/**
 * Hook for Context Management page state
 */
export function useContextManagementState(
  projectId: number,
  options: UseProjectStateOptions = {},
): UseProjectStateReturn<ContextManagementState> {
  return usePageState<ContextManagementState>(projectId, 'context', options);
}

/**
 * Hook for CRA page state
 */
export function useCRAState(
  projectId: number,
  options: UseProjectStateOptions = {},
): UseProjectStateReturn<CRAState> {
  return usePageState<CRAState>(projectId, 'cra', options);
}

/**
 * Hook for ADR page state
 */
export function useADRState(
  projectId: number,
  options: UseProjectStateOptions = {},
): UseProjectStateReturn<ADRState> {
  return usePageState<ADRState>(projectId, 'adrs', options);
}

/**
 * Hook for Generation page state
 */
export function useGenerationState(
  projectId: number,
  options: UseProjectStateOptions = {},
): UseProjectStateReturn<GenerationState> {
  return usePageState<GenerationState>(projectId, 'generation', options);
}

/**
 * Hook for Enhancement page state
 */
/**
 * Hook for Review page state
 */
export function useReviewState(
  projectId: number,
  options: UseProjectStateOptions = {},
): UseProjectStateReturn<ReviewState> {
  return usePageState<ReviewState>(projectId, 'review', options);
}

// ======================
// Generic Page State Hook
// ======================

function usePageState<T>(
  projectId: number,
  page: keyof Omit<ProjectState, 'project' | 'lastSavedAt' | 'version'>,
  options: UseProjectStateOptions = {},
): UseProjectStateReturn<T> {
  const {
    autoSave = true,
    autoSaveDelay = 2000,
    enableErrorRetry = true,
    maxRetries = 3,
  } = options;

  const [state, setState] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  
  const retryCountRef = useRef(0);
  const initialLoadRef = useRef(false);

  // Load initial state
  useEffect(() => {
    if (initialLoadRef.current) {return;}
    initialLoadRef.current = true;

    const loadState = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const pageState = await projectStateService.getPageState<T>(projectId, page);
        setState(pageState);
        setLastSaved(new Date().toISOString());
        retryCountRef.current = 0;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load state';
        setError(errorMessage);
        
        if (enableErrorRetry && retryCountRef.current < maxRetries) {
          retryCountRef.current++;
          setTimeout(loadState, 1000 * retryCountRef.current);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadState();
  }, [projectId, page, enableErrorRetry, maxRetries]);

  // Update state function
  const updateState = useCallback(async (newState: Partial<T>) => {
    try {
      const updatedState = { ...state, ...newState } as T;
      setState(updatedState);
      
      if (autoSave) {
        projectStateService.debouncedSave(projectId, page, updatedState, autoSaveDelay);
      }
      
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update state');
    }
  }, [state, projectId, page, autoSave, autoSaveDelay]);

  // Manual save function
  const saveState = useCallback(async () => {
    if (!state) {return;}
    
    try {
      await projectStateService.savePageState(projectId, page, state);
      setLastSaved(new Date().toISOString());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save state');
      throw err;
    }
  }, [state, projectId, page]);

  // Reset state function
  const resetState = useCallback(() => {
    setState(null);
    setError(null);
    setLastSaved(null);
  }, []);

  return {
    state,
    isLoading,
    error,
    updateState,
    saveState,
    resetState,
    lastSaved,
  };
}

// ======================
// Utility Hooks
// ======================

/**
 * Hook to get project ID from URL params
 */
export function useProjectId(): number | null {
  const { projectId } = useParams<{ projectId?: string }>();
  return projectId ? Number.parseInt(projectId, 10) : null;
}

/**
 * Hook for auto-saving form data
 */
export function useAutoSave<T>(
  data: T,
  saveFunction: (data: T) => Promise<void>,
  delay: number = 2000,
  enabled: boolean = true,
) {
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (!enabled) {return;}
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      try {
        await saveFunction(data);
      } catch (error) {
        logger.error('Auto-save failed:', error);
      }
    }, delay);
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, saveFunction, delay, enabled]);
}

/**
 * Hook for tracking unsaved changes
 */
export function useUnsavedChanges<T>(
  currentData: T,
  savedData: T | null,
  isEqual: (a: T, b: T | null) => boolean = (a, b) => JSON.stringify(a) === JSON.stringify(b),
): boolean {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  useEffect(() => {
    setHasUnsavedChanges(!isEqual(currentData, savedData));
  }, [currentData, savedData, isEqual]);
  
  return hasUnsavedChanges;
}
